import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RotateCcw, Pencil, Trash2, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import type { Transaction, ReturnResult, MasterItem, ColumnFilterOption } from "@/types";
import {
  formatGridDate,
  todayInputDate,
  toInputDate,
  getPeriodRange,
  shiftPeriodRange,
  formatPeriodRangeLabel,
  type PeriodMode,
} from "@/lib/utils";

export default function ReturnRegisterPage() {
  const { api, user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [txnSearch, setTxnSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Transaction[]>([]);
  const [returnResult, setReturnResult] = useState<ReturnResult | null>(null);
  const [returnDate, setReturnDate] = useState(todayInputDate());
  const [showReturnDate, setShowReturnDate] = useState(false);

  // Detail / edit dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailTxn, setDetailTxn] = useState<Transaction | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTxn, setEditingTxn] = useState<Transaction | null>(null);
  const [editForm, setEditForm] = useState({ issue_date: "", due_date: "", return_date: "" });

  // Column filter state (managed centrally, passed to DataTable)
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [periodMode, setPeriodMode] = useState<PeriodMode>("week");
  const [periodRange, setPeriodRange] = useState(() => getPeriodRange("week"));
  const [classes, setClasses] = useState<MasterItem[]>([]);
  const [sections, setSections] = useState<MasterItem[]>([]);

  const txnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch classes & sections for filters
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/masters/classes/all"), api.get("/masters/sections/all")]);
        setClasses(c.data);
        setSections(s.data);
      } catch {}
    })();
  }, [api]);

  const fetchReturned = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20, status: "returned" };
      // Build search from text column filters
      const textParts = [columnFilters.student_name, columnFilters.book_title, columnFilters.accession_no].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.class_section) params.class_id = columnFilters.class_section;
      if (columnFilters.section) params.section_id = columnFilters.section;
      if (periodRange.from) params.from_date = periodRange.from;
      else if (columnFilters.issue_date_from) params.from_date = columnFilters.issue_date_from;
      if (periodRange.to) params.to_date = periodRange.to;
      else if (columnFilters.issue_date_to) params.to_date = columnFilters.issue_date_to;
      const res = await api.get("/transactions", { params });
      setTransactions(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters, periodRange]);

  useEffect(() => {
    fetchReturned();
  }, [fetchReturned]);

  // Debounced column filter change handler
  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    const next = { ...filters };
    if (filters.class_section !== columnFilters.class_section) {
      next.section = "";
    }
    setColumnFilters(next);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, [columnFilters.class_section]);

  const handlePeriodModeChange = (mode: PeriodMode) => {
    setPeriodMode(mode);
    setPeriodRange(getPeriodRange(mode, periodRange.from || new Date()));
    setPage(1);
  };

  const shiftPeriod = (delta: number) => {
    setPeriodRange((prev) => shiftPeriodRange(periodMode, prev.from, delta));
    setPage(1);
  };

  const searchTransactions = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        const res = await api.get("/transactions", { params: { status: "issued", limit: 50, search: q } });
        setSearchResults(res.data.items);
      } catch {}
    },
    [api],
  );

  const handleTxnSearchChange = (v: string) => {
    setTxnSearch(v);
    if (txnTimer.current) clearTimeout(txnTimer.current);
    txnTimer.current = setTimeout(() => searchTransactions(v), 300);
  };

  const resetReturnDialog = () => {
    setTxnSearch("");
    setSearchResults([]);
    setReturnResult(null);
    setReturnDate(todayInputDate());
    setShowReturnDate(false);
  };

  const returnBook = async (txnId: string) => {
    try {
      const res = await api.post("/transactions/return", { transaction_id: txnId, return_date: returnDate });
      setReturnResult(res.data);
      toast.success(`Book returned${res.data.fine_amount > 0 ? ` | Fine: ₹${res.data.fine_amount}` : ""}`);
      setSearchResults([]);
      setTxnSearch("");
      fetchReturned();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Return failed");
    }
  };

  const openDetailTxn = (txn: Transaction) => {
    setDetailTxn(txn);
    setDetailDialogOpen(true);
  };

  const openEditTxn = (txn: Transaction) => {
    setEditingTxn(txn);
    setEditForm({
      issue_date: toInputDate(txn.issue_date),
      due_date: toInputDate(txn.due_date),
      return_date: toInputDate(txn.return_date),
    });
    setDetailDialogOpen(false);
    setEditDialogOpen(true);
  };

  const saveEditTxn = async () => {
    if (!editingTxn) return;
    try {
      await api.put(`/transactions/${editingTxn.id}`, editForm);
      toast.success("Transaction updated");
      setEditDialogOpen(false);
      fetchReturned();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteTxn = async (id: string) => {
    if (!window.confirm("Delete this return record?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Deleted");
      fetchReturned();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const classOptions: ColumnFilterOption[] = classes.map((c) => ({ label: c.name, value: String(c.id) }));
  const sectionOptions: ColumnFilterOption[] = columnFilters.class_section
    ? sections
        .filter((s) => String(s.class_id) === String(columnFilters.class_section))
        .map((s) => ({ label: s.name, value: String(s.id) }))
    : sections.map((s) => ({ label: s.name, value: String(s.id) }));

  const hideOnMobile = "hidden md:table-cell";

  const columns: any[] = [
    {
      accessorKey: "student_name",
      header: "Student",
      meta: { filterVariant: "text", filterPlaceholder: "Student..." },
      cell: ({ row }: any) => {
        const classSection = [row.original.student_class_name, row.original.student_section_name].filter(Boolean).join(" · ");
        return (
          <div className="min-w-0">
            <span className="leading-snug">{row.original.student_name}</span>
            <p className="text-xs text-muted-foreground mt-0.5 md:hidden">{classSection || "—"}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "book_title",
      header: "Book",
      meta: { filterVariant: "text", filterPlaceholder: "Book...", columnClassName: "min-w-[160px] md:min-w-[280px] md:w-[30%]" },
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2 min-w-0">
          {row.original.has_cover && (
            <img
              src={`${api.defaults.baseURL}/books/${row.original.book_id}/cover`}
              alt=""
              className="hidden h-11 w-8 shrink-0 rounded object-cover sm:block"
              onError={(e: any) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="min-w-0">
            <span className="min-w-0 leading-snug block">{row.original.book_title}</span>
            <p className="text-xs text-muted-foreground mt-0.5 md:hidden">
              <span className="font-mono">{row.original.accession_no}</span>
              {" · "}
              {formatGridDate(row.original.issue_date)}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "accession_no",
      header: "Accession No",
      meta: { filterVariant: "text", filterPlaceholder: "Accession...", hideOnMobile: true },
      cell: ({ row }: any) => <span className="font-mono text-xs">{row.original.accession_no}</span>,
    },
    {
      accessorKey: "issue_date",
      header: "Issued",
      meta: { filterVariant: "date-range", hideOnMobile: true },
      cell: ({ row }: any) => formatGridDate(row.original.issue_date),
    },
    {
      accessorKey: "return_date",
      header: "Returned",
      meta: { columnClassName: hideOnMobile },
      cell: ({ row }: any) => formatGridDate(row.original.return_date),
    },
    {
      accessorKey: "fine_amount",
      header: "Fine",
      meta: { columnClassName: hideOnMobile },
      cell: ({ row }: any) =>
        row.original.fine_amount > 0 ? (
          <span className="text-red-600 font-medium">₹{row.original.fine_amount}</span>
        ) : (
          <span className="text-green-600">₹0</span>
        ),
    },
    {
      id: "class_section",
      header: "Class",
      meta: { filterVariant: "select", filterOptions: classOptions, filterPlaceholder: "Class", hideOnMobile: true },
      cell: ({ row }: any) =>
        row.original.student_class_name ? (
          <span className="text-xs">{row.original.student_class_name}</span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    {
      id: "section",
      header: "Section",
      meta: { filterVariant: "select", filterOptions: sectionOptions, filterPlaceholder: "Section", hideOnMobile: true },
      cell: ({ row }: any) =>
        row.original.student_section_name ? (
          <span className="text-xs">{row.original.student_section_name}</span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { columnClassName: hideOnMobile },
      cell: () => <Badge className="bg-green-100 text-green-800 hover:bg-green-100">returned</Badge>,
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            meta: { columnClassName: hideOnMobile },
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditTxn(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteTxn(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="return-register-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight whitespace-nowrap">
            Return Register
          </h1>
        </div>
        {isAdmin && (
          <Button
            className="w-full sm:w-auto h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => {
              resetReturnDialog();
              setReturnDialogOpen(true);
            }}
          >
            <RotateCcw className="w-5 h-5 mr-1.5 shrink-0" /> Process Return
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={transactions}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        toolbarExtra={
          <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
            <div className="inline-flex items-center rounded-md border bg-card p-0.5 shrink-0" data-testid="period-mode-selector">
              {(["week", "month", "year"] as PeriodMode[]).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  variant={periodMode === mode ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2 text-xs capitalize"
                  onClick={() => handlePeriodModeChange(mode)}
                >
                  {mode}
                </Button>
              ))}
            </div>
            <div className="inline-flex items-center gap-0.5 rounded-md border bg-card p-0.5 shrink-0" data-testid="period-range-nav">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => shiftPeriod(-1)}
                title={`Previous ${periodMode}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-1.5 text-xs sm:text-sm font-medium tabular-nums whitespace-nowrap text-center">
                {formatPeriodRangeLabel(periodMode, periodRange.from, periodRange.to)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => shiftPeriod(1)}
                title={`Next ${periodMode}`}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        }
        onRowClick={openDetailTxn}
        data-testid="returned-table"
      />

      {/* Return Dialog */}
      <Dialog
        open={returnDialogOpen}
        onOpenChange={(open) => {
          setReturnDialogOpen(open);
          if (!open) resetReturnDialog();
        }}
      >
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary" /> Process Return
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4" data-testid="return-form">
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-full justify-between text-xs"
                onClick={() => setShowReturnDate((prev) => !prev)}
                data-testid="toggle-return-date-btn"
              >
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {showReturnDate ? "Hide return date" : "Show return date"}
                </span>
                <span className="text-muted-foreground font-normal tabular-nums">{formatGridDate(returnDate)}</span>
              </Button>
              {showReturnDate && (
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1.5 min-w-0">
                    <Label>Return Date</Label>
                    <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="return-date-input" />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Search issued books (type 2+ letters)</Label>
              <Input
                value={txnSearch}
                onChange={(e) => handleTxnSearchChange(e.target.value)}
                placeholder="Student, title, or accession no"
                data-testid="return-search-input"
              />
            </div>

            {searchResults.length > 0 && (
              <div className="border rounded-md bg-card max-h-60 overflow-y-auto">
                {searchResults.map((t) => (
                  <div
                    key={t.id}
                    className="flex flex-col gap-2 border-b px-3 py-2.5 last:border-0 hover:bg-muted sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{t.book_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.student_name} | {t.accession_no} | Due: {formatGridDate(t.due_date)}
                      </p>
                    </div>
                    <Button size="sm" className="w-full bg-primary hover:bg-primary/90 sm:w-auto" onClick={() => returnBook(t.id)}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Return
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {returnResult && (
              <div className="p-3 rounded-md bg-green-50 border border-green-200 text-sm">
                Book returned successfully.
                {returnResult.fine_amount > 0 && <span className="font-semibold text-red-600 ml-1">Fine: ₹{returnResult.fine_amount}</span>}
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Return Details</DialogTitle>
          </DialogHeader>
          {detailTxn && (
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4" data-testid="return-detail-dialog">
              <div className="flex items-start gap-3">
                {detailTxn.has_cover && (
                  <img
                    src={`${api.defaults.baseURL}/books/${detailTxn.book_id}/cover`}
                    alt=""
                    className="w-14 h-20 shrink-0 object-cover rounded"
                    onError={(e: any) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <div className="min-w-0 space-y-1">
                  <p className="font-medium leading-snug">{detailTxn.book_title}</p>
                  <p className="text-sm text-muted-foreground font-mono">{detailTxn.accession_no}</p>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">returned</Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Student</p>
                  <p className="font-medium">{detailTxn.student_name}</p>
                  {detailTxn.student_admission_no && (
                    <p className="text-xs text-muted-foreground font-mono">{detailTxn.student_admission_no}</p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Class / Section</p>
                  <p className="font-medium">
                    {[detailTxn.student_class_name, detailTxn.student_section_name].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.issue_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Due Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.due_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Return Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.return_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Fine</p>
                  <p className={`font-medium ${detailTxn.fine_amount > 0 ? "text-red-600" : "text-green-600"}`}>
                    ₹{detailTxn.fine_amount || 0}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0 !flex-row !flex-nowrap justify-end gap-2 space-x-0">
            <Button
              className="flex-1 sm:flex-none h-11 text-base font-semibold bg-slate-200 text-slate-800 hover:bg-slate-300"
              onClick={() => setDetailDialogOpen(false)}
            >
              Close
            </Button>
            {isAdmin && detailTxn && (
              <Button
                className="flex-1 sm:flex-none h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => openEditTxn(detailTxn)}
              >
                <Pencil className="w-5 h-5 mr-1.5" /> Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Edit Return Record</DialogTitle>
          </DialogHeader>
          {editingTxn && (
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
              <div className="p-2 bg-muted rounded text-sm">
                <span className="font-medium">{editingTxn.book_title}</span> — {editingTxn.student_name}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Issue Date</Label>
                  <Input type="date" value={editForm.issue_date} onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Due Date</Label>
                  <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Return Date</Label>
                  <Input type="date" value={editForm.return_date} onChange={(e) => setEditForm({ ...editForm, return_date: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={saveEditTxn}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
